// Campaign service for fetching and managing campaigns
export type { Campaign } from '../types/Campaign';
import { Campaign as InternalCampaignType } from '../types/Campaign';
import { getApiBaseUrl, buildApiUrl } from '../utils/apiConfig';

// Generic API request function with authentication
async function apiRequest<T>(
  endpoint: string, 
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  data?: any
): Promise<T> {
  const apiUrl = buildApiUrl(endpoint);
  
  console.log(`[Campaign Service] ${method} request to ${apiUrl}`);
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  const requestInit: RequestInit = {
    method,
    headers,
    credentials: 'include', // Include cookies for authentication
  };
  
  if (data && method !== 'GET') {
    requestInit.body = JSON.stringify(data);
  }
  
  try {
    const response = await fetch(apiUrl, requestInit);
    
    if (!response.ok) {
      let errorMessage = `API Error (${response.status})`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If we can't parse JSON, use status text
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    return await response.json() as T;
  } catch (error) {
    console.error(`[Campaign Service] Error in ${method} ${apiUrl}:`, error);
    throw error;
  }
}

// Function to validate campaign data before sending to API
function validateCampaign(campaign: Omit<InternalCampaignType, 'id'>): void {
  // Check required fields
  if (!campaign.name || !campaign.startDate || !campaign.endDate || !campaign.type) {
    throw new Error('Missing required fields: name, startDate, endDate, and type are required');
  }
  
  // Validate campaign name
  if (typeof campaign.name !== 'string' || campaign.name.trim().length < 1 || campaign.name.length > 100) {
    throw new Error('Campaign name must be between 1-100 characters');
  }
  
  // Validate dates
  try {
    const start = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      throw new Error('Invalid date format');
    }
    
    if (end < start) {
      throw new Error('End date cannot be earlier than start date');
    }
  } catch (error) {
    throw new Error('Invalid date format. Please use valid dates.');
  }
  
  // Validate campaign type
  const validTypes = ['promotion', 'content', 'rcf', 'rcf_planner'];
  if (!validTypes.includes(campaign.type)) {
    throw new Error('Campaign type must be one of: "promotion", "content", "rcf", or "rcf_planner"');
  }
  
  // Validate notes if present
  if (campaign.notes !== undefined && campaign.notes !== null && typeof campaign.notes !== 'string') {
    throw new Error('Notes must be a string');
  }
}

// Helper function to get user's timezone
export const getUserTimezone = (): string => {
  try {
    const savedTimezone = localStorage.getItem('preferredTimezone');
    return savedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('Error getting user timezone:', error);
    return 'UTC';
  }
};

// Get all campaigns
export async function getCampaigns(timezone: string): Promise<InternalCampaignType[]> {
  const campaigns = await apiRequest<InternalCampaignType[]>(`/api/campaigns?timezone=${encodeURIComponent(timezone)}`);
  
  // Process each campaign to ensure RCF campaigns have proper rcf_data structure
  return campaigns.map(campaign => {
    // For RCF campaigns, ensure rcf_data has contentDays and promotionDays
    if (campaign.type === 'rcf_planner') {
      const campaignAny = campaign as any;
      const contentDays = campaignAny.content_days || 0;
      const promotionDays = campaignAny.promotion_days || 0;
      
      if (!campaign.rcf_data) {
        campaign.rcf_data = {
          contentDays: contentDays,
          promotionDays: promotionDays
        };
      } else {
        campaign.rcf_data.contentDays = campaign.rcf_data.contentDays || contentDays;
        campaign.rcf_data.promotionDays = campaign.rcf_data.promotionDays || promotionDays;
      }
    }
    
    return campaign;
  });
}

// Helper function to check if campaigns overlap
export function checkForOverlap(
  startDate: Date | string,
  endDate: Date | string,
  campaigns: InternalCampaignType[],
  excludeId?: string
): boolean {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Reset hours for accurate date comparison
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  if (!campaigns || !Array.isArray(campaigns)) {
    console.error('Invalid campaigns data:', campaigns);
    return false;
  }
  
  return campaigns.some(campaign => {
    // Skip the campaign we're updating
    if (excludeId && campaign.id === excludeId) {
      return false;
    }
    
    const campaignStart = new Date(campaign.startDate);
    const campaignEnd = new Date(campaign.endDate);
    
    // Reset hours
    campaignStart.setHours(0, 0, 0, 0);
    campaignEnd.setHours(0, 0, 0, 0);
    
    // Check for overlap
    return (start <= campaignEnd && end >= campaignStart) || 
           (campaignStart <= end && campaignEnd >= start);
  });
}

// Create a new campaign
export async function createCampaign(
  campaignData: Omit<InternalCampaignType, 'id'>, 
  existingCampaigns: InternalCampaignType[],
  timezone: string
): Promise<InternalCampaignType> {
  // Validate campaign data
  validateCampaign(campaignData);
  
  // Check for overlaps
  if (checkForOverlap(campaignData.startDate, campaignData.endDate, existingCampaigns)) {
    throw new Error('This campaign would overlap with another campaign. Each day can only have one campaign scheduled.');
  }
  
  return apiRequest<InternalCampaignType>('/api/campaigns', 'POST', {
    ...campaignData,
    timezone
  });
}

// Update an existing campaign
export async function updateCampaign(
  id: string,
  campaignData: Omit<InternalCampaignType, 'id'>,
  existingCampaigns: InternalCampaignType[],
  timezone: string
): Promise<InternalCampaignType> {
  // Validate campaign data
  validateCampaign(campaignData);
  
  // Check for overlaps with other campaigns (excluding this one)
  const otherCampaigns = existingCampaigns.filter(c => c.id !== id);
  if (checkForOverlap(campaignData.startDate, campaignData.endDate, otherCampaigns)) {
    throw new Error('This campaign would overlap with another campaign. Each day can only have one campaign scheduled.');
  }
  
  return apiRequest<InternalCampaignType>(`/api/campaigns/${id}`, 'PUT', {
    ...campaignData,
    timezone
  });
}

// Delete a campaign
export async function deleteCampaign(id: string): Promise<void> {
  await apiRequest<void>(`/api/campaigns/${id}`, 'DELETE');
}

// Simplified fetch campaigns (alias for getCampaigns)
export const fetchCampaigns = getCampaigns;

// Simplified add campaign (alias for createCampaign)
export const addCampaign = createCampaign;

// RCF Campaign specific functions
export async function createRCFCampaign(campaignData: any, timezone: string): Promise<any> {
  console.log('[Campaign Service] Creating RCF campaign with data:', campaignData);
  
  // Validate basic campaign fields
  if (!campaignData.name || !campaignData.startDate || !campaignData.endDate) {
    throw new Error('Missing required fields: name, startDate, and endDate are required');
  }
  
  // Ensure type is set correctly
  campaignData.type = 'rcf_planner';
  
  // Create the campaign
  const response = await apiRequest<any>('/api/campaigns/rcf', 'POST', {
    ...campaignData,
    timezone
  });
  
  console.log('[Campaign Service] RCF campaign created successfully:', response);
  return response;
}

export async function updateRCFCampaign(id: string, campaignData: any, timezone: string): Promise<any> {
  console.log('[Campaign Service] Updating RCF campaign:', id, campaignData);
  
  // Validate basic campaign fields
  if (!campaignData.name || !campaignData.startDate || !campaignData.endDate) {
    throw new Error('Missing required fields: name, startDate, and endDate are required');
  }
  
  // Ensure type is set correctly
  campaignData.type = 'rcf_planner';
  
  // Update the campaign
  const response = await apiRequest<any>(`/api/campaigns/rcf/${id}`, 'PUT', {
    ...campaignData,
    timezone
  });
  
  console.log('[Campaign Service] RCF campaign updated successfully:', response);
  return response;
}

export async function getRCFCampaignById(id: string): Promise<any> {
  console.log('[Campaign Service] Fetching RCF campaign by ID:', id);
  
  const response = await apiRequest<any>(`/api/campaigns/rcf/${id}`);
  
  console.log('[Campaign Service] RCF campaign fetched:', response);
  return response;
}